import { useEffect } from 'react';
import { di } from 'react-magnetic-di';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { isFieldTypeSupported } from '@atlassian/ui-modifications-core/src/controllers/view-configuration';
import { useIssueAdjustmentsActions } from '@atlassian/ui-modifications-core/controllers/issue-adjustments/main';
import type { InitializeFieldPayload } from '@atlassian/ui-modifications-core/controllers/issue-adjustments/initialize-field';
import { isUserPickerEmailSupportEnabled } from '@helpcenter/feature-flags';
import { REQUEST_CREATE_VIEW_TYPE } from '../../../common/constants';
import type { UserPickerExtraData } from '../../../common/types/extra-data';
import {
	getValueByInitializeFieldPropertyMapping,
	transformInitialValueForField,
	transformUrlParamValueForField,
} from '../../../common/utils/configuration/utils';
import type { FieldState, FieldValues } from 'state/persisted/request-create/types';

type FieldStateWithRendererType = FieldState & {
	rendererType: string;
};

const hasRendererType = (field: FieldState): field is FieldStateWithRendererType => {
	// Indeed it is typed as any, replace with Object.hasOwn once available in help-center.
	// eslint-disable-next-line @typescript-eslint/no-unsafe-return
	return Object.prototype.hasOwnProperty.call(field, 'rendererType');
};

const getFieldConfigId = (field: FieldState): string | undefined =>
	'fieldConfigId' in field && typeof field.fieldConfigId === 'string'
		? field.fieldConfigId
		: undefined;

/**
 * Extra field data for the field configuration's transformers. It is never sent to apps.
 * The user picker passes `fieldConfigId` to the Portal user search, which uses it to apply
 * the field's user filter. Typed with `UserPickerExtraData`, which the user picker's transformer
 * reads with the same type.
 */
const getExtraData = (field: FieldState): UserPickerExtraData | undefined => {
	const fieldConfigId = getFieldConfigId(field);
	return isUserPickerEmailSupportEnabled() && fieldConfigId ? { fieldConfigId } : undefined;
};

export const useInitializeFields = ({
	fields,
	urlParamFieldValueOverrides = {},
}: {
	fields: FieldState[];
	urlParamFieldValueOverrides?: FieldValues;
}) => {
	di(useIssueAdjustmentsActions, isFieldTypeSupported, transformUrlParamValueForField);
	const { initializeFields } = useIssueAdjustmentsActions();

	useEffect(() => {
		const fieldsToInitialize: InitializeFieldPayload[] = fields
			// Remove 'as string' on isUiModificationsSupportEnabled, ditto_uim_-_jsm_support_main FF cleanup
			.filter(({ jiraFieldType }) =>
				isFieldTypeSupported(REQUEST_CREATE_VIEW_TYPE, jiraFieldType as string),
			)
			.map((field) => {
				// When a field value was pre-populated via a URL query parameter, the URL param
				// override (already parsed into the correct internal shape by parseFieldUrlParams /
				// hydrateUrlParamOverrides) takes precedence over the field's server-side default.
				// This is necessary because some field types (e.g. select, multiselect,
				// cascadingselect) store their UIM-visible value in `field.defaultValue` rather
				// than `field.value` (via initializeFieldPropertyMapping), but `defaultValue` is
				// never populated for URL-param-only pre-fills — only `initialFieldValues` is
				// updated in the reducer. Without this override, getValue() returns null in onInit
				// even though the field visually shows the pre-selected option.
				const urlParamOverrideValue: unknown = urlParamFieldValueOverrides[field.id];
				const hasUrlParamOverride =
					fg('jsm_uim_queryparam_prefill_fix') && urlParamOverrideValue !== undefined;

				const fieldType = field.jiraFieldType as string;
				const initializedValue = getValueByInitializeFieldPropertyMapping({
					field,
					fieldType,
					property: 'value',
					defaultValue: null,
				});
				const value = hasUrlParamOverride
					? transformUrlParamValueForField(urlParamOverrideValue, field, fieldType)
					: transformInitialValueForField(initializedValue, field, fieldType);

				return {
					field: {
						value,
						allowedValues: getValueByInitializeFieldPropertyMapping({
							field,
							fieldType: field.jiraFieldType as string,
							property: 'allowedValues',
							defaultValue: undefined,
						}),
						fieldName: field.label,
						fieldId: field.id,
						description: field.description,
						descriptionRaw: field.description,
						isRequired: field.required ?? false,
						isVisible: field.displayed,
						isReadOnly: field.disabled ?? false,
						// Remove 'as string' on isUiModificationsSupportEnabled, ditto_uim_-_jsm_support_main FF cleanup
						fieldType: field.jiraFieldType as string,
					},
					metadata: {
						fieldRenderer: hasRendererType(field) ? field.rendererType : undefined,
						extraData: getExtraData(field),
					},
				};
			});

		initializeFields(fieldsToInitialize);

		// No deps, intialize once
		// oxlint-disable-next-line react-hooks/exhaustive-deps
	}, []);
};
